/**
 * Resuelve subdomain/custom domain → slug, lo deja en ALS + cache sync (rewrite)
 * y setea cookie para hidratación client.
 */
import { createMiddleware } from "@tanstack/react-start";
import { parseTenantHost, normalizeHost } from "@/lib/tenant-host";
import { dbSmResolveSlugBySubdomain, dbSmResolveSlugByCustomDomain } from "@/lib/db-service";
import { hostSlugAls } from "@/lib/tenant-host-slug.server";
import {
  hostSlugCookieHeader,
  rememberHostSlug,
  cachedHostSlug,
  type HostSlugStore,
} from "@/lib/tenant-host-slug";
import { canonicalMarketPath } from "@/lib/storefront-slug-aliases";

async function resolveSlugForHost(rawHost: string): Promise<HostSlugStore | null> {
  const host = normalizeHost(rawHost);
  if (!host) return null;

  const cached = cachedHostSlug(host);
  if (cached) return { host, slug: cached };

  const parsed = parseTenantHost(host);
  let slug: string | null = null;
  if (parsed.kind === "subdomain" && parsed.subdomain) {
    slug = await dbSmResolveSlugBySubdomain(parsed.subdomain).catch(() => null);
  } else if (parsed.kind === "custom") {
    slug = await dbSmResolveSlugByCustomDomain(parsed.host).catch(() => null);
  }
  if (!slug) return null;
  rememberHostSlug(host, slug);
  return { host, slug };
}

export const tenantHostSlugMiddleware = createMiddleware().server(async ({ request, next }) => {
  const rawHost =
    request.headers.get("x-forwarded-host") ?? request.headers.get("host") ?? "";
  const url = new URL(request.url);
  const pathname = url.pathname;
  const canonicalPath = canonicalMarketPath(pathname);
  if (canonicalPath) {
    url.pathname = canonicalPath;
    return {
      response: new Response(null, {
        status: 308,
        headers: { Location: `${url.pathname}${url.search}` },
      }),
    } as never;
  }
  const store = await resolveSlugForHost(rawHost);
  const setCookie = !!store?.slug && !pathname.startsWith("/api/");

  // Cache sync ANTES de next()/rewrite por si el ALS no propaga en el isolate.
  if (store?.slug) rememberHostSlug(store.host, store.slug);

  return hostSlugAls.run(store, async () => {
    const result = await next();
    if (!setCookie || !store?.slug || !result.response) return result;

    const headers = new Headers(result.response.headers);
    headers.append("Set-Cookie", hostSlugCookieHeader(store.slug));
    return {
      ...result,
      response: new Response(result.response.body, {
        status: result.response.status,
        statusText: result.response.statusText,
        headers,
      }),
    };
  });
});
